"use client";

import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@lattice/frontend/components/ui/card";
import { Cookie } from "lucide-react";
import { Button } from "@lattice/frontend/components/ui/button";
import { useLocalStorage } from "@reactuses/core";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { WebAnalyticsTracker } from "@lattice/frontend/components/analytics/web-analytics-tracker";
import type { T_AnalyticsProperties } from "@lattice/frontend/lib/analytics/types";

const ORG_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Lattice Finance",
  url: "https://www.trylattice.io",
  logo: "https://www.trylattice.io/whale-icon.png",
  sameAs: [],
};

export default function CookieConsent({
  is_analytics_enabled,
  mixpanel_api_url,
  mixpanel_mode,
  mixpanel_token,
  clarity_project_id,
  clarity_user_cuid,
  app_environment,
  analytics_user_properties,
}: {
  is_analytics_enabled: boolean;
  mixpanel_api_url?: string;
  mixpanel_mode?: string;
  mixpanel_token?: string;
  clarity_project_id?: string;
  clarity_user_cuid?: string | null;
  app_environment: string;
  analytics_user_properties?: T_AnalyticsProperties;
}) {
  const [isMounted, setIsMounted] = React.useState(false);
  const [consent, setConsent] = useLocalStorage<string | null>(
    "cookie-consent",
    null,
  );
  const pathname = usePathname();

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  // The build-mode landing page never asks. It is a waitlist field for
  // logged-out visitors, and a consent modal over it is the clutter the page
  // exists to avoid. Nothing is given up by staying quiet: consent stays
  // null here, so the optional GTM / GA / Reddit tags below never load and
  // no cookie that would need consent is set. The ask moves to sign-in,
  // where someone is actually starting a session.
  const isConsentPromptSuppressed = pathname === "/";

  const showBanner =
    isMounted && consent === null && !isConsentPromptSuppressed;
  const optionalAnalyticsAllowed =
    isMounted && consent === "true" && is_analytics_enabled;
  const shouldInitializeClarity = isMounted && is_analytics_enabled;

  return (
    <>
      <WebAnalyticsTracker
        appEnvironment={app_environment}
        clarityAnalyticsStorage={
          optionalAnalyticsAllowed ? "granted" : "denied"
        }
        clarityProjectId={clarity_project_id}
        isClarityEnabled={shouldInitializeClarity}
        mixpanelApiUrl={mixpanel_api_url}
        mixpanelMode={mixpanel_mode}
        mixpanelToken={mixpanel_token}
        userCuid={clarity_user_cuid}
        userProperties={analytics_user_properties}
      />
      {showBanner ? (
        <div className="fixed z-100 transition-all duration-700 bottom-0 right-0 sm:right-4 sm:bottom-4 w-full sm:max-w-md">
          <Card className="m-3 shadow-lg bg-background">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 h-0 px-4">
              <CardTitle className="text-base">We use cookies</CardTitle>
              <Cookie className="h-4 w-4" />
            </CardHeader>
            <CardContent className="pt-0 pb-2 px-4">
              <CardDescription className="text-sm">
                We use cookies to ensure you get the best experience on our
                website. For more information on how we use cookies, please see
                our cookie policy.
              </CardDescription>
            </CardContent>
            <CardFooter className="flex gap-2 h-0 py-2 px-4">
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 rounded-full"
                onClick={() => setConsent("false")}
              >
                Decline Optional
              </Button>
              <Button
                size="sm"
                className="flex-1 rounded-full"
                onClick={() => setConsent("true")}
              >
                Accept All
              </Button>
            </CardFooter>
          </Card>
        </div>
      ) : null}
      {optionalAnalyticsAllowed ? (
        <>
          <Script
            id="ld-org"
            type="application/ld+json"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(ORG_JSON_LD),
            }}
          />
          <Script
            id="gtm-script"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','GTM-K424TJMQ');
              `,
            }}
          />
          <Script
            id="gtag-head-script"
            async
            src="https://www.googletagmanager.com/gtag/js?id=G-QQ9QSDTRWW"
          />
          <Script id="gtag-script">
            {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());

                gtag('config', 'G-QQ9QSDTRWW');
              `}
          </Script>
          <Script
            id="reddit-ads-script"
            dangerouslySetInnerHTML={{
              __html: `
                !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);rdt('init','a2_i2bqbuzij0bw');rdt('track', 'PageVisit');
              `,
            }}
          />
          <noscript>
            <iframe
              src="https://www.googletagmanager.com/ns.html?id=GTM-K424TJMQ"
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            />
          </noscript>
        </>
      ) : null}
    </>
  );
}
